import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import pluralize from "pluralize";
import dayjs from "dayjs";
import {
  AssignToRequest,
  AssignToTarget,
} from "../../../services/prePlanningService.types";
import { ServiceCenter } from "../../../types/serviceCenter.type";
import { useSections } from "../../../hooks/react-query/preplanning/useSections";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { useAllRoutesTable } from "../../../hooks/react-query/useDeliveryRoutes";
import Loading from "../../../components/shared/layout/Loading";
import { useGetInboundTraps } from "../../../hooks/react-query/preplanning/useTraps";
import { toast } from "../../../utils/snackbarHelper";
import { LoadingButton } from "../../../components/shared/LoadingButton";
import SelectTrap from "../../../components/shared/SelectTrap";
import { toastMessage } from "../../../constants/strings";
import DropdownSelect from "../../../components/shared/DropdownSelect";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import DropdownStatusItem from "../../../components/shared/DropdownStatusItem";
import { useAssignShipments } from "../../../hooks/react-query/useAssignShipments";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import { getAfterRouteStopParam } from "../inboundPlanning/utils";
import { useTableSelectionParam } from "../../../hooks/useTableSelectionParam";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { SummaryContext, useActionsMenuContext } from "./ActionsMenuContext";
import ClosedStatusWarning from "./ClosedStatusWarning";
import AssignToDialogV2 from "./AssignToDialogV2";

type SelectBoxProps = {
  sic: ServiceCenter;
  date: string;
  handleTarget: (target: AssignToTarget) => void;
};

const TrapSelectBox = ({ sic, handleTarget, date }: SelectBoxProps) => {
  const isCNCLTrapFeatureEnabled = useIsFeatureFlagEnabled(
    "operations-5594-CNCL-traps-treatment-client"
  );

  const { data: traps = [], isLoading } = useGetInboundTraps({
    sicId: sic.id,
    date: dateToInt(date),
  });

  const availableTraps = isCNCLTrapFeatureEnabled
    ? traps.filter((trap) => trap.status !== "CNCL")
    : traps;

  const defaultOptionText = "Select a Trap";
  const enabled = availableTraps.length > 0;
  const title = "Trap";

  if (isLoading) return <Loading />;

  return (
    <Box>
      <Typography fontSize={14} fontWeight={600} mb={1}>
        {title}
      </Typography>
      <FormControl
        sx={{ width: "100%" }}
        size="small"
        disabled={!enabled}
        required
      >
        <InputLabel id={`select-${defaultOptionText}-label`} htmlFor="trap">
          {defaultOptionText}
        </InputLabel>
        <SelectTrap
          id="trap"
          label={defaultOptionText}
          disabled={!enabled}
          title={title}
          traps={availableTraps}
          size="small"
          onTrapChange={(event) => {
            const trapId = event.toString();
            handleTarget({
              id: trapId,
              type: "trap",
              date: dateToInt(date),
              sicId: sic.id,
            });
          }}
        />
      </FormControl>
    </Box>
  );
};

const RouteSelectBox = ({ date, sic, handleTarget }: SelectBoxProps) => {
  const { data: routes = [], isLoading } = useAllRoutesTable({
    date: dateToInt(date),
    sicId: sic.id,
  });
  const [target, setTarget] = useState("");

  if (isLoading) return <Loading />;

  // Filter out routes in DEPT status (immutable targets)
  const availableRoutes = routes.filter((route) => route.status !== "DEPT");
  return (
    <DropdownSelect
      disabled={availableRoutes.length === 0}
      title="Route"
      label="Select a Route"
      options={availableRoutes.map((route) => ({
        name: route.title,
        value: route.id,
        bills: route.bills,
        weight: route.weight,
        pallets: route.pallets,
        pieces: route.pieces,
        unit: route.shipmentsWeightUnit,
        metadata: {
          status: route.status,
        },
      }))}
      value={target}
      onChange={(event) => {
        const value = String(event.target.value);
        setTarget(value);
        handleTarget({
          id: value,
          type: "route",
          date: dateToInt(date),
          sicId: sic.id,
        });
      }}
      required
      renderOption={({
        name,
        bills,
        weight,
        pallets,
        pieces,
        unit,
        metadata,
      }) => (
        <DropdownStatusItem
          label={name}
          shipmentDetails={{ bills, weight, pallets, pieces, unit }}
          status={metadata.status}
          bgColor="green"
          color="white"
        />
      )}
    />
  );
};

const SectionSelectBox = ({ sic, handleTarget, date }: SelectBoxProps) => {
  const { data: sections = [], isLoading } = useSections(sic.id);
  const [target, setTarget] = useState("");

  if (isLoading) return <Loading />;

  return (
    <DropdownSelect
      disabled={sections.length === 0}
      title="Section"
      label="Select a Section"
      options={sections.map((section) => ({
        name: section.title,
        value: section.id,
      }))}
      value={target}
      onChange={(event) => {
        const value = String(event.target.value);
        setTarget(value);
        handleTarget({
          id: value,
          type: "section",
          date: dateToInt(date),
          sicId: sic.id,
        });
      }}
      required
    />
  );
};

type AssignToDialogProps = {
  onSuccess?: () => void;
  source?: "trap" | "route";
};

export const AssignToDialog = ({ onSuccess, source }: AssignToDialogProps) => {
  const [serviceCenter] = useSelectedServiceCenter();
  const {
    closeDialog,
    shipments,
    clearSelection,
    isUpdatingClosedRouteOrTrap,
  } = useActionsMenuContext();

  const isOsadFeatureEnabled = useIsFeatureFlagEnabled(
    "inbound-5931-OSAD-assign-form-client"
  );

  const [date] = useDateSearchParamOrFallbackToToday();
  const dayName = dayjs.utc(date).format("dddd");
  const isWeekend = dayName === "Saturday" || dayName === "Sunday";
  const planDate = dateToInt(date);
  const [confirmOpen, setConfirmOpen] = useState(isWeekend);
  const { clearSelection: clearTableSelection } = useTableSelectionParam();

  const [openAssignToDialog, setOpenAssignToDialog] = useState(
    isWeekend ? false : true
  );

  const determineInitialAssignValue = useCallback(
    (shipments: SummaryContext[]): string => {
      const allDeliveryRoutes = shipments.every(
        (shipment) => shipment.type === "route" || shipment.type === "shipment"
      );
      return allDeliveryRoutes ? "route" : "trap";
    },
    []
  );

  const initialAssignValue = useMemo(
    () => determineInitialAssignValue(shipments),
    [shipments, determineInitialAssignValue]
  );

  const [type, setType] = useState(source ? source : initialAssignValue);
  const [target, setTarget] = useState<AssignToTarget>({
    id: "0",
    type: "none",
    date: planDate,
    sicId: serviceCenter.id,
  });

  const showClosedStatusWarning = isUpdatingClosedRouteOrTrap(target.id);

  const handleTarget = (target: AssignToTarget) => {
    setTarget(target);
  };

  const createAssignToRequest = (target: AssignToTarget): AssignToRequest => {
    const origins = shipments.map((shipment) => ({
      id: shipment.id.toString(),
      shipmentIds: shipment.shipmentIds,
      type: shipment.type,
    }));
    return {
      origins: [...origins],
      target: {
        id: target.id || "0",
        type: target.type || "none",
        date: planDate,
        sicId: serviceCenter.id,
        ...getAfterRouteStopParam(null),
      },
    };
  };

  const assignShipments = useAssignShipments({
    onSuccess: ({ affectedShipmentsCount }) => {
      clearSelection?.();
      clearTableSelection();
      if (onSuccess) onSuccess();
      toast(toastMessage.inbound.assign.success(affectedShipmentsCount), {
        variant: "success",
      });
      closeDialog();
    },
  });

  const uniqueShipmentIds = new Set(shipments.flatMap((s) => s.shipmentIds));

  const handleAssignTo = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (uniqueShipmentIds.size === 0) {
      toast(toastMessage.inbound.assign.noShipmentsToAssign, {
        variant: "warning",
      });
    } else {
      assignShipments.mutate(createAssignToRequest(target));
    }
  };

  const handleCloseDialog = () => {
    closeDialog();
    assignShipments.reset();
  };

  return (
    <>
      {isWeekend && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            closeDialog();
          }}
          title="Add to Plan"
          message={
            <>
              You are adding shipments to a plan on a <strong>{dayName}</strong>
              . Do you want to proceed?
            </>
          }
          cancelButtonText="No"
          confirmButtonLabel="Yes"
          confirmButtonLoadingLabel="Proceeding…"
          isConfirmPending={false}
          onConfirm={() => {
            setConfirmOpen(false);
            setOpenAssignToDialog(true);
          }}
        />
      )}
      {isOsadFeatureEnabled ? (
        <AssignToDialogV2 open={openAssignToDialog} onClose={closeDialog} />
      ) : (
        <Dialog open={openAssignToDialog} onClose={closeDialog}>
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Assign {pluralize("Shipment", uniqueShipmentIds.size)}
          </DialogTitle>
          {showClosedStatusWarning && <ClosedStatusWarning />}
          {assignShipments.isPending ? (
            <>
              <DialogContent
                sx={{ mt: 3, display: "flex", justifyContent: "center" }}
              >
                <Loading />
              </DialogContent>
              <DialogActions sx={{ margin: "10px 20px 20px 20px" }}>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancel
                </Button>
              </DialogActions>
            </>
          ) : (
            <form onSubmit={handleAssignTo}>
              <DialogContent sx={{ width: "60vw", maxWidth: "500px" }}>
                <Typography sx={{ fontWeight: "bold" }}>
                  Assign {pluralize("shipment", uniqueShipmentIds.size, true)}{" "}
                  to:
                </Typography>
                <FormControl sx={{ marginBottom: 2 }}>
                  <RadioGroup
                    aria-labelledby="assign-to-radio-buttons-group-label"
                    defaultValue="trap"
                    name="assign-to-radio-buttons-group"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <FormControlLabel
                      value="trap"
                      control={<Radio />}
                      label="Trap"
                    />
                    <FormControlLabel
                      value="route"
                      control={<Radio />}
                      label="Route"
                    />
                    {/** 
                 * TODO remove this comment when assigning section is available on the API
                 <FormControlLabel
                  value="section"
                  control={<Radio />}
                  label="Section"
                /> */}
                  </RadioGroup>
                </FormControl>
                {type === "trap" ? (
                  <TrapSelectBox
                    date={date}
                    sic={serviceCenter}
                    handleTarget={handleTarget}
                  />
                ) : type === "route" ? (
                  <RouteSelectBox
                    sic={serviceCenter}
                    handleTarget={handleTarget}
                    date={date}
                  />
                ) : type === "section" ? (
                  <SectionSelectBox
                    date={date}
                    sic={serviceCenter}
                    handleTarget={handleTarget}
                  />
                ) : (
                  ""
                )}
              </DialogContent>
              <DialogActions sx={{ margin: "10px 20px 20px 20px" }}>
                <LoadingButton
                  loadingLabel="Assigning..."
                  isLoading={assignShipments.isPending}
                >
                  Assign
                </LoadingButton>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancel
                </Button>
              </DialogActions>
            </form>
          )}
        </Dialog>
      )}
    </>
  );
};
